<template>
  <div class="barcode-scanner-wager-ticket-content">
    <div class="my-bets bold-title">
      {{ $t('BetSlip.Open Bets') }}: {{ ticketOpenBets.length }} | {{ $t('BetSlip.Graded Bets') }}:
      {{ ticketGradedBets.length }}
    </div>

    <div class="bet-content-table-area">
      <div class="info-area">
        <div class="row">
          <div class="col-6">
            <div class="item">
              <div class="key">{{ $t('Risk') }}:</div>
              <div class="value">
                <price :val="totalRisk" />
              </div>
            </div>

            <div class="item">
              <div class="key">{{ $t('Win') }}:</div>
              <div class="value">
                <price :val="totalWin" />
              </div>
            </div>
          </div>
          <div class="col-6">
            <div class="item" v-if="hasPaid">
              <div class="key" style="color: green !important">{{ $t('Wager.Paid') }}</div>
            </div>
            <div class="item" v-if="cancelDate">
              <div class="key">{{ $t('Wager.Cancelled') }}</div>
              <div class="value">{{ $moment(cancelDate).format('DD-MM-YYYY HH:mm') }}</div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-for="(bet, i) in [...ticketOpenBets, ...ticketGradedBets]"
        :key="'ticket-o:' + i"
        class="p-1 d-flex flex-column"
      >
        <div class="content-title">{{ bet.wager_header.complete_description }}</div>

        <div class="info-area info-area-bet">
          <div class="row">
            <div class="col-3">
              <div class="item">
                <div class="key">{{ $t('Risk') }}:</div>
                <div class="value">
                  <price :val="bet.sold_amount" />
                </div>
              </div>

              <div class="item">
                <div class="key">{{ $t('Win') }}:</div>
                <div class="value">
                  <price :val="calculateReturnValue(bet)" />
                </div>
              </div>
            </div>

            <div class="col-3" v-if="!isZero(bet.TotalTaxWin) || !isZero(bet.TotalTaxRisk)">
              <div class="item" v-if="!isZero(bet.TotalTaxWin)">
                <div class="key">{{ $t('Wager.TotalTaxWin') }}:</div>
                <div class="value">
                  <price :val="bet.TotalTaxWin" />
                </div>
              </div>

              <div class="item" v-if="!isZero(bet.TotalTaxRisk)">
                <div class="key">{{ $t('Wager.TotalTaxRisk') }}:</div>
                <div class="value">
                  <price :val="bet.TotalTaxRisk" />
                </div>
              </div>
            </div>

            <div class="col-3" v-if="!isZero(bet.TotalFeeWin) || !isZero(bet.TotalFeeRisk)">
              <div class="item" v-if="!isZero(bet.TotalFeeWin)">
                <div class="key">{{ $t('Wager.TotalFeeWin') }}:</div>
                <div class="value">
                  <price :val="bet.TotalFeeWin" />
                </div>
              </div>

              <div class="item" v-if="!isZero(bet.TotalFeeRisk)">
                <div class="key">{{ $t('Wager.TotalFeeRisk') }}:</div>
                <div class="value">
                  <price :val="bet.TotalFeeRisk" />
                </div>
              </div>
            </div>

            <div class="col-3">
              <div class="item" v-if="bet.Status === 'W' && bet.payout_id">
                <div class="key" style="color: green !important">{{ $t('Wager.Paid') }}</div>
              </div>
            </div>
          </div>
        </div>

        <table class="w-100">
          <thead>
            <tr>
              <th class="th-1">{{ $t('WagerModal.Status') }}</th>
              <th class="th-2">{{ $t('WagerModal.EventType') }}</th>
              <th class="th-3">{{ $t('WagerModal.EventInformation') }}</th>
              <th class="th-4">{{ $t('Wager.ExpirationDate') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(detailed, index) in bet.wager_header.wager_details"
              :key="'ticket-o:' + detailed.detail_description_c + '-' + index"
              class=""
            >
              <td>
                <div class="d-flex align-items-center">
                  <div
                    class="status-box"
                    :class="{
                      win: status(detailed).win,
                      lost: status(detailed).lost,
                      push: status(detailed).push,
                    }"
                  ></div>
                  {{ status(detailed).text }}
                </div>
              </td>
              <td>
                {{ detailed.is_live ? $t('WagerModal.LiveMatch') : $t('WagerModal.PreMatch') }}
              </td>
              <td>{{ detailed.detail_description_c.replace(/~~/g, ' | ') + ' | ' + getOdd(detailed.odds) }}</td>
              <td>
                {{ bet.expiration_date ? $moment(bet.expiration_date).format('DD-MM-YYYY HH:mm') : '' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import snq from 'snq';
import { mapState } from 'vuex';
import { roundAmount, americanToDecimal, fractionFromUS } from '@/views/sports/components/bet-slip/bet-slip-utils';

export default {
  name: 'TicketLineDetails',
  props: ['ticketGradedBets', 'ticketOpenBets', 'scanDetailResponse'],
  computed: {
    ...mapState({
      scanCode: (state) => state.clerkPayoutDepositTicketStore.scanCode,
      terminalInfo: (state) => state.settings.terminalInfo,
      selectedOddType: (state) => state.settings.selectedOddType,
    }),
    totalWin() {
      const bets = [...this.ticketGradedBets, ...this.ticketOpenBets];
      let returnValue = 0;
      bets.forEach((bet) => {
        returnValue += this.calculateReturnValue(bet);
      });
      return returnValue;
    },
    totalRisk() {
      return snq(() => this.scanDetailResponse.data.ticket_detail.SoldAmount);
    },
    ticketDetail() {
      return snq(() => this.scanDetailResponse.data.ticket_detail);
    },
    totalPaidAmount() {
      this.$store.commit('clerkDepositStore/SET_TICKET_PAID_BY', this.ticketDetail.paid_by)
      return this.ticketDetail.bets.reduce((bet, acc) => parseFloat(bet.paid_amount) + acc, 0);
    },
    hasPaid() {
      return parseFloat(this.totalPaidAmount) > 0;
    },
    cancelDate() {
      return snq(() => this.ticketDetail.cancellation_date.date);
    },
  },
  methods: {
    getOdd(val) {
      if (this.selectedOddType == 'D') {
        return roundAmount(americanToDecimal(val));
      } else if (this.selectedOddType == 'E') {
        return val > 0 ? `+${val}` : val;
      } else {
        return fractionFromUS(val);
      }
    },
    isZero(value) {
      if (!parseFloat(value)) return true;
    },
    calculateReturnValue(bet) {
      if (!bet) return;
      const paidAmount = bet.paid_amount;
      if (parseFloat(paidAmount) > 0) {
        return paidAmount;
      }
      const totalWinTaxAndFee = parseFloat(bet.total_tax_win) + parseFloat(bet.total_fee_win);
      const returnValue = parseFloat(bet.wager_header.risk) + parseFloat(bet.wager_header.win);
      return parseFloat(returnValue) - parseFloat(totalWinTaxAndFee);
    },
    calculateRiskValue(bet) {
      // means parlay
      if (bet.WagerType === '1') {
        return bet.Detail[0].SoldAmount;
      }
      // means teaser
      if (bet.WagerType === '2') {
        return bet.Detail[0].SoldAmount;
      }
      // means if_bet_reverse
      if (bet.WagerType === '4') {
        return bet.Detail[0].SoldAmount;
      }
      return bet.Detail.reduce((acc, cur) => acc + parseFloat(cur.SoldAmount), 0);
    },
    status(detailed) {
      if (detailed.status_text == 'push') {
        return {
          text: this.$t('BarcodeModal.Push'),
          push: true,
        };
      }
      if (detailed.status_text == 'pending') {
        return {
          text: this.$t('BarcodeModal.Pending'),
          pending: true,
        };
      }
      if (detailed.status_text == 'win') {
        return {
          text: this.$t('BarcodeModal.Win'),
          win: true,
        };
      }
      if (detailed.status_text == 'lose') {
        return {
          text: this.$t('BarcodeModal.Lost'),
          lost: true,
        };
      }

      return {
        text: null,
        pending: false,
        win: false,
        lost: false,
      };
    },
  },
};
</script>

<style lang="scss">
.barcode-scan-modal {
  .modal__wrapper {
    max-height: 80% !important;
  }
  .modal__container {
    width: 1200px !important;
    max-width: 1200px !important;
    max-height: 100% !important;

    .modal__header {
      color: var(--color3);
      text-transform: uppercase;
      letter-spacing: 0;
      margin: 0;
      font-size: 18px;
      font-weight: bold;
      line-height: 20px;
    }

    .modal__body {
      height: 100%;
      overflow-y: auto;

      .confirmation-wrapper {
        text-align: left;
        position: relative;

        .wager-modal-bottom-area {
          position: sticky;
          bottom: 0px;
          background-color: var(--color4);
          padding-top: 10px;
        }
        .ticket-information {
          font-weight: 600;
        }
        .my-bets {
          padding-bottom: 20px;
          font-size: 1rem;
        }
        .bet-title {
          background-color: #e8e8e8;
          font-weight: 900;
          border-radius: 6px;
          text-transform: uppercase;
          padding: 5px 14px;
          span {
            color: var(--brandColor);
            font-weight: 600;
          }
        }

        .description,
        .risk-amount {
        }
      }
    }
  }
}

.bet-content-table-area {
  .content-title {
    font-weight: bold;
    margin-bottom: 10px;
  }
  table {
    border-collapse: collapse;
    margin-bottom: 10px;

    td,
    th {
      border: 1px solid #ddd;
      padding: 5px;
    }
    .th-1 {
    }
    .th-2 {
    }
    .th-3 {
      white-space: nowrap;
    }
    .th-4 {
    }
    tbody tr {
      background-color: #f2f2f2;
    }
    tbody tr:hover {
      background-color: #efefef;
    }

    thead {
      font-weight: bold;
      background-color: var(--color1);
      color: #fff;
      font-size: 12px;
      white-space: nowrap;
    }
  }
}

.status-box {
  height: 10px;
  width: 10px;
  border: 1px solid #ddd;
  margin-right: 5px;

  &.push {
    border: none;
    background-color: #2f56c6;
  }

  &.win {
    border: none;
    background-color: #1cd04c;
  }

  &.lost {
    border: none;
    background-color: #ca2323;
  }
}

.info-area {
  margin-bottom: 10px;
  color: #555;
  width: 90%;

  &.info-area-bet {
  }

  .item {
    display: flex;
    justify-content: space-between;
    margin-right: 10px;
  }
  .key {
    font-weight: bold;
  }

  .value {
  }
}
</style>
